import React from 'react';
import clsx from 'clsx';
import { InitialAssignableType } from '@/components/shared/LessonOverview/Cards/AssignButton';
import { tv } from 'tailwind-variants';
import useFlags from '@/hooks/useFlags';
import CardTitle from './CardTitle';
import ActionBar from '../ActionBar';

const iconVariants = tv({
  base: '',
  variants: {
    reflow: {
      false: 'tw-w-16',
      true: 'tw-w-10 tw-ml-2 tw-mr-2.5 reflow-narrow:tw-w-16 card-2col:tw-mx-4',
    },
  },
});

interface FullWidthCardProps {
  actionBarChildren?: React.ReactNode;
  borderLeft?: boolean;
  icon: React.ReactNode;
  iconSrc?: string;
  initialValues?: InitialAssignableType;
  isAssigned?: boolean;
  isStaffer?: boolean;
  pacing?: string;
  page?: number;
  presentPath: string;
  programColor?: string;
  title: string;
  // Rendered between the title and the action bar. Give it its own shrink behavior — it shares the
  // row with the title, which keeps its 2-line clamp.
  titleAdjacent?: React.ReactNode;
  openInNewWindow?: boolean;
}

const FullWidthCard = ({
  borderLeft = false, openInNewWindow = false,
  actionBarChildren, icon, initialValues, isAssigned, isStaffer, pacing, page, presentPath, title, programColor,
  titleAdjacent,
}: FullWidthCardProps) => {
  const { accessibilityReflow } = useFlags();
  const target = openInNewWindow ? '_blank' : '';

  return (
    <section
      aria-label={title}
      className={clsx(
        'card tw-relative tw-h-24 tw-my-2 card-2col:tw-mx-2 tw-rounded-md tw-overflow-hidden', {
          'tw-border-l-[0.25rem] tw-border-l-solid': borderLeft,
        },
      )}
      style={{ borderLeftColor: borderLeft && `#${programColor}` }}
    >
      <div className="tw-flex tw-h-full">
        <div className={clsx(
          'tw-flex tw-items-center tw-flex-shrink-0 card-2col:tw-w-24',
          iconVariants({ reflow: accessibilityReflow }),
        )}
        >
          <div
            className="tw-flex tw-w-full tw-py-2 tw-justify-center"
          >
            <a aria-hidden="true" href={presentPath} tabIndex={-1} target={target}> {icon} </a>
          </div>
        </div>
        <div
          className={clsx('tw-grow tw-min-w-0 tw-overflow-hidden tw-ml-1 tw-my-4 card-2col:tw-ml-5', {
            '!tw-my-auto': !page && !pacing,
          })}
        >
          <CardTitle
            fullWidth
            halfCard
            linkHref={presentPath}
            linkLabel={title}
            openInNewWindow={openInNewWindow}
            page={page}
            programColor={programColor}
            time={pacing}
          />
        </div>
        {titleAdjacent}
        <div className="tw-ml-auto tw-my-auto [&>div]:tw-px-1 [&>div]:tw-mx-0 card-2col:[&>div]:tw-px-4 card-2col:[&>div]:tw-mx-2">
          <ActionBar
            href={presentPath}
            iconType="play"
            initialValues={initialValues}
            isAssigned={isAssigned}
            isStaffer={isStaffer}
          >
            {actionBarChildren}
          </ActionBar>
        </div>
      </div>
    </section>
  );
};

export default FullWidthCard;
